import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { Figure, useCodeLabel, useFormats } from '@/pages/bacen/shared';
import { fetchCrsfnRelator } from '@/services/bacen';

/**
 * One relator on the linked acordaos: the caseload by year, the appeal-type
 * and disposition mixes side by side, how often the bench ruled against the
 * relator's own vote (a count), and the member's participations on other
 * relators' acordaos. Below the floor the record is listed and nothing is
 * rated; the refusal is record-scoped.
 */
export function CrsfnRelatorPage() {
  const { slug = '' } = useParams();
  const { t } = useTranslation(['bacen', 'common']);
  const { number, percent } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'crsfn', 'relator', slug],
    queryFn: () => fetchCrsfnRelator(slug),
    enabled: slug.length > 0,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="p-6">
        <ErrorMessage
          message={notFound ? t('bacen:crsfnRelator.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const name = data.profile.value?.name ?? (data.profile.reason_params?.name as string) ?? slug;

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link
            to="/bacen/crsfn/relators"
            className="text-primary underline-offset-2 hover:underline"
          >
            {t('bacen:crsfnRelator.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold text-foreground">{name}</h1>
        <p className="text-sm text-muted-foreground">{t('bacen:crsfnRelator.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="bacen:crsfnRelator.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <Answer answer={data.profile} scope="record" help={t('bacen:crsfnRelator.help')}>
        {(p) => (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Figure
                label={t('bacen:crsfnRelator.acordaos')}
                value={number.format(p.acordaos)}
                second={`${p.first_year ?? '—'}–${p.last_year ?? '—'} · ${t('bacen:crsfnRelator.sessions', { n: number.format(p.sessions) })}`}
                testId="acordaos"
              />
              <Figure
                label={t('bacen:crsfnRelator.withDispositive')}
                value={`${number.format(p.with_dispositive)} (${percent.format(
                  p.acordaos ? p.with_dispositive / p.acordaos : 0,
                )})`}
                second={t('bacen:crsfnRelator.unanimity', {
                  unanimous: number.format(p.unanimous),
                  majority: number.format(p.majority),
                })}
              />
              <Figure
                label={t('bacen:crsfnRelator.overruled')}
                value={number.format(p.overruled)}
                second={t('bacen:crsfnRelator.overruledNote')}
                testId="overruled"
              />
              <Figure
                label={t('bacen:crsfnRelator.participations')}
                value={number.format(p.participations)}
                second={t('bacen:crsfnRelator.vencido', {
                  n: number.format(p.vencido),
                })}
              />
            </dl>

            <div className="grid gap-4 lg:grid-cols-2">
              <section className="rounded-lg border bg-card p-4" data-appeal-types>
                <h2 className="mb-2 text-sm font-semibold text-foreground">
                  {t('bacen:crsfnRelator.appealTypes')}
                </h2>
                <ul className="space-y-1 text-sm">
                  {p.appeal_types.map((r) => (
                    <li key={r.code} className="flex justify-between gap-3">
                      <span className="text-muted-foreground">{label('appealType', r.code)}</span>
                      <span className="tabular-nums text-foreground">
                        {number.format(r.acordaos)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
              <section className="rounded-lg border bg-card p-4" data-dispositions>
                <h2 className="mb-2 text-sm font-semibold text-foreground">
                  {t('bacen:crsfnRelator.dispositions')}
                </h2>
                <ul className="space-y-1 text-sm">
                  {p.dispositions.map((r) => (
                    <li key={r.code} className="flex justify-between gap-3">
                      <span className="text-muted-foreground">{label('disposition', r.code)}</span>
                      <span className="tabular-nums text-foreground">
                        {number.format(r.acordaos)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('bacen:crsfnRelator.mixNote')}
                </p>
              </section>
            </div>

            <section className="rounded-lg border bg-card p-4" data-by-year>
              <h2 className="mb-2 text-sm font-semibold text-foreground">
                {t('bacen:crsfnRelator.byYear')}
              </h2>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('bacen:crsfnRelator.year')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnRelator.acordaos')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnRelator.overruled')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.by_year.map((y) => (
                      <tr key={y.year} className="border-t" data-year={y.year}>
                        <td className="px-3 py-2 text-foreground">{y.year}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(y.acordaos)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(y.overruled)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </Answer>
      <FieldGuide i18nKey="bacen:crsfnRelator.guide" />
    </div>
  );
}
